import React, { useEffect, useState } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import WaitlistModal from './WaitlistModal';

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * AuthGuard - Checks user approval status immediately after authentication
 * Blocks access to the app if user is on waitlist or rejected
 */
const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistPosition, setWaitlistPosition] = useState<number | undefined>();
  const [isRejected, setIsRejected] = useState(false);

  useEffect(() => {
    if (!isLoaded || !user) {
      return;
    }

    const metadata = user.publicMetadata || {};
    const approvalStatus = metadata.approvalStatus as string | undefined;

    // Users without approvalStatus are legacy users (auto-approved)
    if (!approvalStatus || approvalStatus === 'approved') {
      setShowWaitlist(false);
      setIsRejected(false);
      return;
    }

    // User is on waitlist
    if (approvalStatus === 'pending') {
      setShowWaitlist(true);
      setWaitlistPosition(metadata.waitlistPosition as number | undefined);
      return;
    }

    // User is rejected
    if (approvalStatus === 'rejected') {
      setIsRejected(true);
      setShowWaitlist(true);
      return;
    }
  }, [user, isLoaded]);

  // Show loading state while checking
  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // User is not signed in - show children (will show sign-in UI)
  if (!user) {
    return <>{children}</>;
  }

  // User is on waitlist or rejected - show modal and block access
  if (showWaitlist) {
    return (
      <>
        <div className="min-h-screen bg-slate-950 flex items-center justify-center">
          <div className="text-center max-w-md p-8">
            {isRejected ? (
              <>
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-red-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Access Not Approved</h2>
                <p className="text-slate-400 mb-6">
                  Your application to access TradeRewind was not approved at this time.
                  If you believe this is an error, please contact support.
                </p>
                <button
                  onClick={() => signOut()}
                  className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-semibold transition-all"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-blue-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">You're on the Waitlist</h2>
                {waitlistPosition && (
                  <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-sm">Your Position</span>
                      <span className="text-2xl font-bold text-blue-400">#{waitlistPosition}</span>
                    </div>
                  </div>
                )}
                <p className="text-slate-400 mb-6">
                  Thank you for signing up! Your account is pending approval. We'll send you an email
                  at <span className="text-white">{user.primaryEmailAddress?.emailAddress}</span> once
                  you're approved.
                </p>
                <button
                  onClick={() => signOut()}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl font-semibold transition-all"
                >
                  Sign Out
                </button>
                <p className="text-xs text-slate-500 mt-4">
                  Questions? Contact support@tradevision.ai
                </p>
              </>
            )}
          </div>
        </div>
        <WaitlistModal
          isOpen={false}
          onClose={() => {}}
          waitlistPosition={waitlistPosition}
          email={user.primaryEmailAddress?.emailAddress}
        />
      </>
    );
  }

  // User is approved - show app
  return <>{children}</>;
};

export default AuthGuard;
